import React from 'react';
import DOMPurify from 'dompurify';

interface FormattedDescriptionProps {
  text: string;
  className?: string;
  darkMode?: boolean;
}

export const FormattedDescription: React.FC<FormattedDescriptionProps> = ({
  text,
  className = '',
  darkMode = true,
}) => {
  if (!text) return null;

  // Check if text is HTML formatted (contains tags like <b>, <strong>, <i>, <span>, <p>, etc.)
  const containsHTML = /<[a-z][\s\S]*>/i.test(text);

  if (containsHTML) {
    const sanitizeFn = typeof DOMPurify.sanitize === 'function'
      ? DOMPurify.sanitize
      : (typeof DOMPurify === 'function' ? (DOMPurify as any)().sanitize : (html: string) => html);
    const cleanHtml = sanitizeFn ? sanitizeFn(text, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'font'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'color', 'size'],
    }) : text;
    return (
      <div
        className={`leading-normal space-y-1 ${darkMode ? 'text-slate-200' : 'text-slate-800'} rich-text-content [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_li]:list-item [&_b]:font-extrabold [&_strong]:font-extrabold ${className}`}
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
      />
    );
  }

  // Clean up any weird multiple asterisks like ****text**** -> **text**
  const cleanedText = text
    .replace(/\*{4,}/g, '**')
    .replace(/\*{3}/g, '***');

  const lines = cleanedText.split('\n');

  return (
    <div className={`space-y-1 leading-normal rich-text-content ${className}`}>
      {lines.map((line, lineIdx) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return <div key={lineIdx} className="h-0.5" />;
        }

        // Check for bullet point format
        const isBullet = trimmed.startsWith('•') || trimmed.startsWith('- ') || (trimmed.startsWith('* ') && !trimmed.endsWith('*'));
        const lineContent = isBullet
          ? trimmed.replace(/^[•\-\*]\s*/, '')
          : line;

        const parts = parseInlineFormatting(lineContent);

        if (isBullet) {
          return (
            <div key={lineIdx} className="flex items-start gap-1.5 pl-1 my-0.5">
              <span className="text-cyan-400 font-extrabold shrink-0">•</span>
              <div className="flex-1">{renderParts(parts, darkMode)}</div>
            </div>
          );
        }

        return (
          <p key={lineIdx} className={trimmed.startsWith('#') ? 'font-black text-base my-0.5' : ''}>
            {renderParts(parts, darkMode)}
          </p>
        );
      })}
    </div>
  );
};

interface InlinePart {
  type: 'bold' | 'italic' | 'text';
  content: string;
}

function parseInlineFormatting(text: string): InlinePart[] {
  const result: InlinePart[] = [];
  // Matches **bold** or *italic*
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push({
        type: 'text',
        content: text.substring(lastIndex, match.index),
      });
    }

    const matchedStr = match[0];
    if (matchedStr.startsWith('**') && matchedStr.endsWith('**') && matchedStr.length >= 4) {
      result.push({
        type: 'bold',
        content: matchedStr.slice(2, -2),
      });
    } else if (matchedStr.startsWith('*') && matchedStr.endsWith('*') && matchedStr.length >= 2) {
      result.push({
        type: 'italic',
        content: matchedStr.slice(1, -1),
      });
    } else {
      result.push({
        type: 'text',
        content: matchedStr,
      });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    result.push({
      type: 'text',
      content: text.substring(lastIndex),
    });
  }

  return result;
}

function renderParts(parts: InlinePart[], darkMode: boolean) {
  return parts.map((part, idx) => {
    if (part.type === 'bold') {
      return (
        <strong
          key={idx}
          className={`font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}
        >
          {part.content}
        </strong>
      );
    }
    if (part.type === 'italic') {
      return (
        <em key={idx} className="italic font-medium opacity-90">
          {part.content}
        </em>
      );
    }
    return <span key={idx}>{part.content}</span>;
  });
}
